import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useSpreadsheetPrefetch } from './hooks/useSpreadsheetPrefetch';
import PrefetchIndicator from './components/PrefetchIndicator';
import Layout from './components/Layout';
import DataLoader from './components/DataLoader';
import DataLoaderWithQuery from './components/DataLoaderWithQuery';
import Dashboard from './components/Dashboard';
import Settings from './components/Settings';
import AIAssistant from './components/AIAssistantFullPage';
import LoginForm from './components/Login';
import MFAVerification from './components/MFAVerification';
import Reporting from './components/Reporting';
import AdvisorResourses from './components/AdvisorResourses';
import MarketingTemplateForm from './components/Marketing';
import FeedbackPage from './components/FeedbackPage';
import { fetchUserYears } from './api/mastersheetApi';
import HelpPage from './components/HelpPage';
import FrequentlyAskedQuestions from './components/FrequentlyAskedQuestions';
import RequirementsCalendar from './components/RequirementsCalendar';
import EducationalEvents from './components/EducationalEvents';
import ParticipantCensus from './components/ParticipantCensus';
import PlanDocuments from './components/PlanDocuments';
import Spreadsheet from './components/ReactSpreadsheet2';
import AdvisorFeeCalculator from './components/AdvisorFeeCalculator';
import CalculatorPage from './components/CalculatorPage';
import FeeCalculator from './components/FeeCalculator';
import ProfitabilityCalculator from './components/ProfitabilityCalculator';
import RetirementReadinessCalculator from './components/RetirementReadinessCalculator';
import RevenueForecastCalculator from './components/RevenueForecastCalculator';
import TPAClientOnboarding from './components/TPAClientOnboarding';
import TPARequirements from './components/TPARequirements';
import TPA5500Automation from './components/TPA5500Automation';
import './styles/animations.css';
import { jwtDecode } from 'jwt-decode'; // Correct import for named export
import ChatWrapper from './components/ChatWrapper';
import SignupForm from './components/Signup';
import SignupSuccess from './components/SignupSuccess';
import whyDidYouRender from "@welldone-software/why-did-you-render";
import dayjs from 'dayjs';
// import FeeBenchmarking from './components/feeBenchmarking';
import Footer from './components/Footer';
import AdminSignupForm from './components/AdminSignup';
import PlanSearchAutomation from './components/PlanSearchAutomation';
import FundResearch from './components/FundResearch';
import WholesalerSearch from './components/WholesalerSearch'; 
import ProposalAutomation from './components/ProposalAutomation';
import { BlurProvider } from './contexts/BlurContext';
import { CompanyProvider } from './contexts/CompanyContext';
if (process.env.NODE_ENV !== 'production') {
  whyDidYouRender(React);
}

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 60 * 1000, // 30 minutes - data is considered fresh for 30 mins
      cacheTime: 60 * 60 * 1000, // 60 minutes - keep in cache for 1 hour
      refetchOnWindowFocus: false,
      refetchOnMount: false, // Don't refetch when component mounts if data exists
      refetchOnReconnect: false, // Don't refetch on reconnect
      retry: 1,
    },
  },
});

// Component to handle prefetching
const PrefetchWrapper = ({ children, selectedYear }) => {
  const { isPrefetching, prefetchProgress } = useSpreadsheetPrefetch(selectedYear);
  return (
    <>
      <PrefetchIndicator isPrefetching={isPrefetching} progress={prefetchProgress} />
      {children}
    </>
  );
};

const App = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [activeSpreadsheet, setActiveSpreadsheet] = useState('Client List');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState(() => localStorage.getItem('username') || '');
  const [selectedYear, setSelectedYear] = useState(null);
  const [years, setYears] = useState([]);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('access_token');
      const storedUsername = localStorage.getItem('username');
      const userid = localStorage.getItem('userid');
      
      if (token && userid) {
        try {
          const decodedToken = jwtDecode(token);
          if (decodedToken.exp * 1000 > Date.now()) {
            setIsAuthenticated(true);
            if (storedUsername) {
              setUsername(storedUsername);
            }
          } else {
            // Token is expired
            clearUserData();
          }
        } catch (error) {
          console.error('Invalid token:', error);
          clearUserData();
        }
      } else {
        clearUserData();
      }
    };

    const clearUserData = () => {
      localStorage.removeItem('access_token');
      localStorage.removeItem('username');
      localStorage.removeItem('userid');
      setIsAuthenticated(false);
      setUsername('');
    };

    checkAuth();
  }, []);

  useEffect(() => {
    if (username) {
      localStorage.setItem('username', username);
      fetchUserYears(username).then(fetchedYears => {
        setYears(fetchedYears);
        setSelectedYear(dayjs().year());
      }).catch(error => console.error("Error fetching user years:", error));
    }
  }, [username]);

  const spreadsheets = [
    { id: 'Client List', name: 'Client List' },
    { id: 'Plan Touchpoints', name: 'Plan Touchpoints' },
    { id: 'Requirements Checklist', name: 'Requirements Checklist' },
    { id: 'Plan Design and Elections', name: 'Plan Design and Elections' },
    { id: 'Plan Performance', name: 'Plan Performance' },
    { id: 'Fund Performance', name: 'Fund Performance' },
    { id: 'Advisor Service Schedule', name: 'Advisor Service Schedule' },
    { id: 'Fee Schedule', name: 'Fee Schedule' },
    { id: 'File Library', name: 'File Library' },
  ];

  // Update the handleLogin function
  const handleLogin = (newToken, newUsername) => {
    localStorage.setItem('access_token', newToken);
    localStorage.setItem('username', newUsername);
    setUsername(newUsername);
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('https://plansync-backend-234288438538.us-central1.run.app/api/auth/logout', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Logout failed');
      }

      // Clear local storage
      localStorage.removeItem('access_token');
      localStorage.removeItem('userid');
      localStorage.removeItem('username');

      // Reset state
      setUsername('');
      setIsAuthenticated(false);
      setActiveSpreadsheet('Client List');

      // Redirect to login page
      window.location.href = '/login';
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <div className="flex flex-col min-h-screen">
          {isAuthenticated ? (
            <CompanyProvider>
              <BlurProvider>
                <PrefetchWrapper selectedYear={selectedYear}>
                <div className="flex flex-col min-h-screen">
                  <Layout
              isSidebarOpen={isSidebarOpen}
              setIsSidebarOpen={setIsSidebarOpen}
              spreadsheets={spreadsheets}
              activeSpreadsheet={activeSpreadsheet}
              setActiveSpreadsheet={setActiveSpreadsheet}
              onLogout={handleLogout}
              username={username}
              years={years}
              selectedYear={selectedYear}
              setSelectedYear={setSelectedYear}
            >
              <div className="flex-grow">
                <Routes>
                  <Route path="/" element={<Navigate to="/spreadsheet/client-list" />} />
                  <Route 
                    path="/spreadsheet/:spreadsheetName" 
                    element={<DataLoaderWithQuery 
                      activeSpreadsheet={activeSpreadsheet} 
                      username={username} 
                      year={selectedYear} 
                      isSidebarOpen={isSidebarOpen}
                    />} 
                  />
                  <Route path="/file-library" element={<PlanDocuments />} />
                  <Route path="/data-dashboard" element={<Dashboard />} />
                  <Route path="/reporting" element={<Reporting />} />
                  <Route path="/ai-assistant" element={<AIAssistant />} />
                  <Route path="/resources" element={<AdvisorResourses />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/marketing" element={<MarketingTemplateForm />} />
                  <Route path="/feedback" element={<FeedbackPage />} />
                  <Route path="/react-spreadsheet" element={<Spreadsheet />} />
                  <Route path="/help" element={<HelpPage />} />
                  <Route path="/faqs" element={<FrequentlyAskedQuestions />} />
                  <Route path="/requirements-calendar" element={<RequirementsCalendar />} />  
                  <Route path="/calculators" element={<CalculatorPage />} />
                  <Route path="/calculators/fee-calculator" element={<FeeCalculator />} />
                  <Route path="/calculators/profitability-calculator" element={<ProfitabilityCalculator />} />
                  <Route path="/calculators/retirement-readiness" element={<RetirementReadinessCalculator />} />
                  <Route path="/calculators/revenue-forecast" element={<RevenueForecastCalculator />} />
                  <Route path="/educational-events" element={<EducationalEvents />} />
                  <Route path="/participant-census" element={<ParticipantCensus />} />
                  <Route path="/tpa-client-onboarding" element={<TPAClientOnboarding />} />
                  <Route path="/tpa-requirements" element={<TPARequirements />} />
                  <Route path="/form-5500-filing" element={<TPA5500Automation />} />
                  <Route path="/plan-search" element={<PlanSearchAutomation />} />
                  <Route path='/fund-research' element={<FundResearch />} />
                  <Route path='/wholesaler-search' element={<WholesalerSearch />} />
                  <Route path='/proposal-automation' element={<ProposalAutomation />} />
                  <Route
                    path="/plan-touchpoints"
                    element={<Navigate to="/spreadsheet/plan-touchpoints" />}
                  />
                  <Route path="*" element={<Navigate to="/" />} />
                </Routes>
                <ChatWrapper />
              </div>
              {/* <Footer /> */}
              {/* COMMENTED THIS OUT TEMPORARILY TO MAKE THE MAIN CONTENT STICKY */}
            </Layout>
          </div>
        </PrefetchWrapper>
      </BlurProvider>
    </CompanyProvider>
        ) : (
          <Routes>
            <Route path="/sign-up" element={<SignupForm />} />
            <Route path="/sign-up/admin" element={<AdminSignupForm />} />
            <Route path="/signup-success" element={<SignupSuccess />} />
            <Route path="/login" element={<LoginForm onLogin={handleLogin} />} />
            <Route path="/mfa-verification" element={<MFAVerification />} />
            <Route path="*" element={<Navigate to="/login" />} />
          </Routes>
        )}
      </div>
    </Router>
    {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
};

export default App;
